import { hasPendingEvents } from 'common/checkout-utils';
import { error, ValidationError } from 'common/errors';
import {
  getRememberMeType,
  pushCheckoutResponse,
  pushCheckoutRequest,
  pushVisaRiskStepUp,
  pushErrors
} from 'common/gamp';
import { propertyIsDefined } from 'common/type-guards';
import { isEmail, isMobileFormatE164, isValidNumber } from 'common/validators';
import checkoutIntegratedEnrollCardLegacy from 'communicator/external/checkout/integrated/legacy/enroll-card-legacy';
import { reset, saveUser } from 'communicator/store';
import {
  getAccessToken,
  getCheckoutEnrollPayload,
  getComplianceResource,
  getFullAccessToken,
  getIsFeatureToggleEnabled,
  getSessionId,
  getTncEntryInfo,
  getTncStatus,
  getUser
} from 'communicator/store/selectors';
import { fetchCheckoutEnroll } from 'communicator/utils/api/checkout';
import { acceptTnc } from 'communicator/utils/api/tnc';
import { getCheckoutErrorMessage } from 'communicator/utils/checkout-error';
import { markEnd, markStart } from 'communicator/utils/instrumentation';
import unbind from 'communicator/utils/unbind';
import { AuthenticationMethod } from 'types/emvco';
import { RememberMeType } from 'types/gamp';
import { RequiredField } from 'types/utility';
import {
  CheckoutCommunicatorPayload,
  CheckoutData,
  CheckoutIntegratedPayload
} from 'types/visa-sdk/checkout';

export default async function checkoutIntegratedEnrollCard(
  checkoutData: RequiredField<CheckoutData, 'encryptedCard'>,
  launchCvv: () => void,
  launchDcf: () => void,
  launchSRCBankApp: (authenticationMethod: AuthenticationMethod) => void,
  launchSRCIssuerOtp: (authenticationMethod: AuthenticationMethod) => void
): Promise<CheckoutCommunicatorPayload | ValidationError> {
  const endMarker = getFullAccessToken()
    ? 'CHECKOUT_INTEGRATED_ENROLL_CARD_RETURNING'
    : 'CHECKOUT_INTEGRATED_ENROLL_CARD_NEW';

  if (!propertyIsDefined(checkoutData, 'consumer')) {
    markEnd(endMarker, {
      doFlush: true,
      failure: true,
      reason: 'CARD_ADD_FAILED'
    });

    // START GENAI@GHCOPILOT
    pushErrors('CARD_ADD_FAILED', 'Missing checkoutData.consumer');
    // END GENAI@GHCOPILOT
    return error('CARD_ADD_FAILED', 'Missing checkoutData.consumer');
  }

  const { consumerIdentity } = checkoutData.consumer;

  if (consumerIdentity) {
    const { mobileNumber, emailAddress = '' } = checkoutData.consumer;
    const isEmailIdentity = consumerIdentity.identityType === 'EMAIL_ADDRESS';

    if ((isEmailIdentity && !mobileNumber?.phoneNumber) || (!isEmailIdentity && !emailAddress)) {
      markEnd(endMarker, {
        doFlush: true,
        failure: true,
        reason: 'INVALID_PARAMETER'
      });

      const missingField = isEmailIdentity
        ? 'consumer.mobileNumber.phoneNumber'
        : 'consumer.emailAddress';

      // START GENAI@GHCOPILOT
      pushErrors('CARD_ADD_FAILED: INVALID_PARAMETER', `${missingField} is required`);
      // END GENAI@GHCOPILOT
      return error('INVALID_PARAMETER', `${missingField} is required`);
    }

    // START GENAI@GHCOPILOT
    const finalMobileNumber = `${mobileNumber?.countryCode || ''}${mobileNumber?.phoneNumber.replace(/^\+/, '') || ''}`;
    // END GENAI@GHCOPILOT

    const isInvalidMobileNumber =
      getIsFeatureToggleEnabled('enableMobileAsId') &&
      isEmailIdentity &&
      (!isMobileFormatE164(finalMobileNumber) || !isValidNumber(finalMobileNumber));

    const isInvalidEmailAddress = !isEmailIdentity && !isEmail(emailAddress);

    if (isInvalidMobileNumber || isInvalidEmailAddress) {
      markEnd(endMarker, {
        doFlush: true,
        failure: true,
        reason: 'ID_FORMAT_UNSUPPORTED'
      });

      const errorMsg = isInvalidMobileNumber
        ? 'Invalid format: consumer.mobileNumber'
        : 'Invalid format: consumer.emailAddress';

      // START GENAI@GHCOPILOT
      pushErrors('CARD_ADD_FAILED: ID_FORMAT_UNSUPPORTED', errorMsg);
      // END GENAI@GHCOPILOT
      return error('ID_FORMAT_UNSUPPORTED', errorMsg);
    }
  }

  const user = getUser();

  if (
    !getIsFeatureToggleEnabled('orchestratorCheckoutEnrollApi') ||
    (user?.isRecognizedUser && !getIsFeatureToggleEnabled('orchestratorCheckoutEnrollRecognized'))
  ) {
    return checkoutIntegratedEnrollCardLegacy(
      checkoutData,
      launchSRCIssuerOtp,
      launchSRCBankApp,
      launchCvv,
      launchDcf
    );
  }

  if (getTncStatus() === 'not_accepted') {
    const tncError = error(
      'CARD_ADD_FAILED',
      'One or more required parameter missing, please check missing fields'
    );

    if (!getComplianceResource('TERMS_AND_CONDITIONS')) {
      markEnd(endMarker, {
        doFlush: true,
        failure: true,
        reason: 'CARD_ADD_FAILED'
      });

      // START GENAI@GHCOPILOT
      pushErrors('CARD_ADD_FAILED', 'Missing compliance resource: TERMS_AND_CONDITIONS');
      // END GENAI@GHCOPILOT
      return tncError;
    }

    const accessTokenState = getAccessToken();
    const tncEntryInfo = getTncEntryInfo();
    markStart('checkout_integrated_accept_tnc');
    const tncResponse = await acceptTnc({ accessToken: accessTokenState?.token, tncEntryInfo });
    markEnd('checkout_integrated_accept_tnc');

    if (!tncResponse.ok) {
      markEnd(endMarker, {
        doFlush: true,
        failure: true,
        reason: 'CARD_ADD_FAILED'
      });

      // START GENAI@GHCOPILOT
      pushErrors(
        'CARD_ADD_FAILED: ACCEPT_TNC',
        'Unable to accept TNC, please check missing fields'
      );

      // END GENAI@GHCOPILOT
      return tncError;
    }

    saveUser({
      accessToken: {
        expiresIn: tncResponse.data.expires_in,
        token: tncResponse.data.access_token,
        isPartial: false
      },
      tnc: {
        status: 'accepted',
        tncEntryInfo
      }
    });
  }

  const uctpAddCard = getIsFeatureToggleEnabled('uctpAddCard');

  const checkoutResponse = await fetchCheckoutEnroll({
    accessToken: getFullAccessToken(),
    payload: await getCheckoutEnrollPayload(),
    sessionId: getSessionId(),
    uctpAddCard: uctpAddCard || false,
    vsbFlow: checkoutData.vsbFlow || false
  });

  if (!checkoutResponse.ok) {
    const messageDetails = getCheckoutErrorMessage(checkoutResponse);
    markEnd(endMarker, {
      doFlush: true,
      failure: true,
      reason: messageDetails.reason
    });

    // START GENAI@GHCOPILOT
    pushErrors(messageDetails.reason, messageDetails.message);
    // END GENAI@GHCOPILOT
    return error(messageDetails.reason, messageDetails.message);
  }

  //START GENAI@GHCOPILOT
  if (uctpAddCard && checkoutResponse.enrollFlow === 'UCTP' && checkoutResponse.data.cardBrand) {
    // @ts-expect-error - UCTP response is incompatible with EMVCo, returning OrchestratorCheckoutEnrollResponse, not CheckoutCommunicatorPayload
    return checkoutResponse;
  }
  //END GENAI@GHCOPILOT

  const rememberMe: RememberMeType = getRememberMeType(checkoutData.complianceSettings);

  pushCheckoutRequest(true, rememberMe);
  const stime = Date.now();
  switch (checkoutResponse.data.riskDecision) {
    case 'REQUIRED3DS':
    case 'REQUIRED_BIOMETRIC':
    case 'REQUIRED_CVV':
    case 'REQUIRED_ISSUER_CTF':
    case 'REQUIRED_VISA_OTP':
      markEnd(endMarker, { doFlush: true, failure: true });
      pushVisaRiskStepUp(
        true,
        rememberMe,
        undefined,
        checkoutResponse.data.riskDecision,
        Date.now() - stime
      );

      // START GENAI@GHCOPILOT
      pushErrors(
        'CARD_ADD_FAILED: RISK_DECISION',
        `Risk decision: ${checkoutResponse.data.riskDecision}`
      );

      // END GENAI@GHCOPILOT
      return error('RISK_DECISION', checkoutResponse.data.riskDecision);
  }

  markEnd(endMarker, { doFlush: true });
  pushCheckoutResponse(
    checkoutResponse.ok,
    true,
    rememberMe,
    undefined,
    undefined,
    Date.now() - stime
  );

  const unbindAppInstance = !user?.isRecognizedUser && !getComplianceResource('REMEMBER_ME');

  if (!hasPendingEvents(checkoutResponse.data.checkoutResponse)) {
    if (unbindAppInstance) {
      unbind({ accessToken: getFullAccessToken() });
    }

    reset();
  }

  const checkoutPayload: CheckoutIntegratedPayload = {
    checkoutResponse: checkoutResponse.data.checkoutResponse,
    dcfActionCode: 'COMPLETE',
    unbindAppInstance
  };

  if (checkoutData.idToken) {
    checkoutPayload.idToken = checkoutData.idToken;
  }

  return {
    payload: checkoutPayload
  };
}
